import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import * as g from '../../globals';
import { RecordModel } from 'src/app/layout/record-model';
import { FilterFieldValue, QueryParams } from '../subscription/classes';
import { tap } from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class InventoryService {
  barcodeResultQuery = 2650;
  sourceDocumentHeaderDetailsQuery = 2654;
  sourceDocumenOpenItemsQuery = 2649;
  serialNumberDetailsQuery = 2655;
  batchNumberDetailsQuery = 2656;
  checkScannedValueQuery = 2659;
  itemNumberDetailsAfterScan = 2660;
  serialNumberDetailsAfterScan = 2661;
  ssccDetailsAfterScan = 2662;
  batchNumberDetailsAfterScan = 2663;
  boxDetailsAfterScan = 80091;
  palletDetailsAfterScan = 80101;
  batchNumberDetailsAfterScan_counting = 2858;
  stockCountingFilterFieldsSection = 10218;
  stockCountingItemColSection = 10219;

  createManualSSCCFilterFieldsSection = 10330;
  createManualSSCCColSection = 10328;
  createManualSSCCQueryId = 2865;

  createManualSSCCResColSection = 10329;

  pushDataToBackend(
    jsonToPush: string,
    actionCode: string,
    dataToProcess: string
  ) {
    return this.http.post(
      `${g.apiBaseUrl}data/PushDataToBackend`,
      JSON.stringify({ jsonToPush, actionCode, dataToProcess })
    );
  }

  constructor(private router: Router, private http: HttpClient) {}

  getHeaderData(sourceTable: string, selectedId: number) {
    return this.http.get(
      `${g.apiBaseUrl}data/executequery?id=${this.sourceDocumentHeaderDetailsQuery}&parameters[0].Key=ObjectKey&parameters[0].Value=${selectedId}&parameters[1].Key=SourceTable&parameters[1].Value=${sourceTable}`
    );
  }

  getOpenLines(sourceTable: string, selectedId: number) {
    return this.http.get(
      `${g.apiBaseUrl}data/executequery?id=${
        this.sourceDocumenOpenItemsQuery
      }&parameters[0].Key=ObjectKey&parameters[0].Value=${selectedId}&parameters[1].Key=SourceTable&parameters[1].Value=${sourceTable.substring(
        1,
        4
      )}`
    );
  }

  getSerialNumberDetails(
    itemCode: string,
    whsCode: string,
    serialNumber: string
  ) {
    return this.http.get(
      `${g.apiBaseUrl}data/executequery?id=${this.serialNumberDetailsQuery}&parameters[0].Key=ItemCode&parameters[0].Value=${itemCode}&parameters[1].Key=WhsCode&parameters[1].Value=${whsCode}&parameters[2].Key=SerialNumber&parameters[2].Value=${serialNumber}`
    );
  }

  getBatchNumberDetails(
    itemCode: string,
    whsCode: string,
    uomEntry: string,
    uomCode: string,
    batchNumber: string
  ) {
    return this.http.get(
      `${g.apiBaseUrl}data/executequery?id=${this.batchNumberDetailsQuery}&parameters[0].Key=ItemCode&parameters[0].Value=${itemCode}&parameters[1].Key=WhsCode&parameters[1].Value=${whsCode}&parameters[2].Key=BatchNumber&parameters[2].Value=${batchNumber}&parameters[3].Key=UomEntry&parameters[3].Value=${uomEntry}&parameters[4].Key=UomCode&parameters[4].Value=${uomCode}`
    );
  }

  clearScanCache() {
    this.scannedValueCache = new Map<string, any>();
  }
  private scannedValueCache = new Map<string, any>();
  getScannedValueType(scan: string, objectKey: string) {
    // Check if the result is already cached
    if (this.scannedValueCache.has(scan)) {
      // Return cached value as an Observable
      return of(this.scannedValueCache.get(scan));
    }

    //new
    if (scan.indexOf(';') !== -1) {
      this.scannedValueCache.set(scan, 'QR');
      return of(this.scannedValueCache.get(scan));
    }

    // Construct the URL
    const url = `${g.apiBaseUrl}data/executescalar?id=${this.checkScannedValueQuery}&parameters[0].Key=Scan&parameters[0].Value=${scan}&parameters[1].Key=ObjectKey&parameters[1].Value=${objectKey}`;

    // Perform HTTP GET and cache the result
    return this.http.get(url).pipe(
      tap((result) => {
        this.scannedValueCache.set(scan, result);
      })
    );
  }

  private itemNumberDetailsCache = new Map<string, any>();
  getItemNumberDetailsAfterScan(scan: string) {
    // Check if scan value is already cached
    if (this.itemNumberDetailsCache.has(scan)) {
      return of(this.itemNumberDetailsCache.get(scan));
    }

    const url = `${g.apiBaseUrl}data/executequery?id=${this.itemNumberDetailsAfterScan}&parameters[0].Key=Scan&parameters[0].Value=${scan}`;

    return this.http.get(url).pipe(
      tap((result) => {
        this.itemNumberDetailsCache.set(scan, result);
      })
    );
  }

  getSerialNumberDetailsAfterScan(scan: string) {
    return this.http.get(
      `${g.apiBaseUrl}data/executequery?id=${this.serialNumberDetailsAfterScan}&parameters[0].Key=Scan&parameters[0].Value=${scan}`
    );
  }

  getBoxDetailsAfterScan(scan: string) {
    return this.http.get(
      `${g.apiBaseUrl}data/executequery?id=${this.boxDetailsAfterScan}&parameters[0].Key=Scan&parameters[0].Value=${scan}`
    );
  }

  getPalletDetailsAfterScan(scan: string) {
    return this.http.get(
      `${g.apiBaseUrl}data/executequery?id=${this.palletDetailsAfterScan}&parameters[0].Key=Scan&parameters[0].Value=${scan}`
    );
  }

  getBatchNumberDetailsAfterScan(scan: string, isCounting: boolean) {
    if (isCounting) {
      return this.http.get(
        `${g.apiBaseUrl}data/executequery?id=${this.batchNumberDetailsAfterScan_counting}&parameters[0].Key=Scan&parameters[0].Value=${scan}`
      );
    } else {
      return this.http.get(
        `${g.apiBaseUrl}data/executequery?id=${this.batchNumberDetailsAfterScan}&parameters[0].Key=Scan&parameters[0].Value=${scan}`
      );
    }
  }

  getSSCCDetailsAfterScan(scan: string) {
    return this.http.get(
      `${g.apiBaseUrl}data/executequery?id=${this.ssccDetailsAfterScan}&parameters[0].Key=Scan&parameters[0].Value=${scan}`
    );
  }

  createObject(recordModel: RecordModel) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.post(`${g.apiBaseUrl}data/CreateObject`, recordModel);
  }

  moduleInformation(moduleId: string) {
    return this.http.get(
      `${g.apiBaseUrl}layout/moduleInformation?id=${moduleId}`
    );
  }

  getFilteredBatches(filterData: FilterFieldValue[]) {
    const queryParams = new QueryParams();
    queryParams.QueryId = this.createManualSSCCQueryId;
    queryParams.FilterFields = filterData;

    return this.http.post(
      `${g.apiBaseUrl}data/GetDataFromQuery?page=0&pageSize=0`,
      JSON.stringify(queryParams)
    );
  }
}
